import { CommonHelper } from '@ptvn-react/authentication';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  LocalStorageName,
  MobileWidthSize,
  TabletWidthSize,
} from '../../utils/constants/CommonConstants';
import Currency from '../../utils/constants/CurrencyConstant';
import { MenuModule } from './HeaderNavMenu';
import { Subscription } from 'rxjs';
import { PIClient } from '../../api/PIClient';
import { useLocation } from 'react-router-dom';
import PageRoute from '../../utils/constants/PageRoute';
import { CompanySubscriptionPackageSettingResponse } from '../../api/response/AuthorizeEMarketplace';

interface HeaderTitle {
  node: React.ReactNode;
  to?: string;
  id?: string;
}

interface UpdateQuotationInf {
  erfxNo?: string;
  erfxType?: string;
}

interface ICartEventLitener {
  productCart?: Date;
  supplierCart?: Date;
}

type CurrencyCodeOption = keyof typeof Currency;
export interface EMarketplaceLayoutContextType {
  title?: HeaderTitle;
  setTitle: React.Dispatch<React.SetStateAction<HeaderTitle | undefined>>;

  role?: string;
  setRole: React.Dispatch<React.SetStateAction<string>>;

  navbar?: React.ReactNode;
  setNavbar: React.Dispatch<React.SetStateAction<React.ReactNode>>;

  navMenu?: React.ReactNode;
  setNavMenu: React.Dispatch<React.SetStateAction<React.ReactNode>>;

  breadcrumb?: { to?: string; title: string }[];
  setBreadcrumb: React.Dispatch<React.SetStateAction<EMarketplaceLayoutContextType['breadcrumb']>>;

  currencyCodeSelected?: CurrencyCodeOption;
  setCurrencyCodeSelected: (val: CurrencyCodeOption | undefined) => void;

  currencyCodeOptions: CurrencyCodeOption[];
  setCurrencyCodeOptions: React.Dispatch<React.SetStateAction<CurrencyCodeOption[]>>;

  isMobileSize: boolean;
  isTabletSize: boolean;
  updateQuotationInfo?: UpdateQuotationInf;
  setUpdateQuotationInfo: React.Dispatch<React.SetStateAction<UpdateQuotationInf | undefined>>;
  resetUpdateQuotationInfoOnAlertBar: () => void;

  cartEventLastUpdate?: ICartEventLitener;
  setCartEventLastUpdate: React.Dispatch<React.SetStateAction<ICartEventLitener | undefined>>;

  activeMenuModule?: MenuModule;
  setActiveMenuModule: React.Dispatch<React.SetStateAction<MenuModule | undefined>>;

  banner?: React.ReactNode;
  setBanner: React.Dispatch<React.SetStateAction<React.ReactNode>>;

  count: number;
  reFetch: () => void;

  countBookmark: number;
  reFetchCountBookmark: () => void;

  orgID: string;
  setOrgID: React.Dispatch<React.SetStateAction<string>>;

  subscriptionPlan: CompanySubscriptionPackageSettingResponse | undefined;
}

const EMarketplaceLayoutContext = React.createContext<EMarketplaceLayoutContextType | undefined>(
  undefined,
);

const CURRENCY_LOCAL_STORAGE_KEY = 'currencyCode';

export const EMarketplaceLayoutProvider = (
  props: React.PropsWithChildren<{
    subscriptionInfo?: CompanySubscriptionPackageSettingResponse;
  }>,
) => {
  const location = useLocation();
  const isPIService = location.pathname.includes(PageRoute.PI);
  const [title, setTitle] = useState<HeaderTitle>();
  const [role, setRole] = useState<string>('');
  const [navbar, setNavbar] = useState<React.ReactNode>();
  const [navMenu, setNavMenu] = useState<React.ReactNode>();
  const [breadcrumb, setBreadcrumb] = useState<EMarketplaceLayoutContextType['breadcrumb']>();
  const [isMobileSize, setIsMobileSize] = useState(false);
  const [isTabletSize, setIsTabletSize] = useState(false);
  const [cartEventLastUpdate, setCartEventLastUpdate] = useState<ICartEventLitener>();

  const [subscriptionPlan, setsubscriptionPlan] = useState<
    CompanySubscriptionPackageSettingResponse | undefined
  >();

  const [updateQuotationInfo, setUpdateQuotationInfo] = useState<UpdateQuotationInf>();
  const [currencyCodeSelected, _setCurrencyCodeSelected] = useState<CurrencyCodeOption | undefined>(
    localStorage.getItem(CURRENCY_LOCAL_STORAGE_KEY) as CurrencyCodeOption | undefined,
  );
  const [currencyCodeOptions, setCurrencyCodeOptions] = useState<CurrencyCodeOption[]>([]);
  const [activeMenuModule, setActiveMenuModule] = useState<MenuModule>();
  const [banner, setBanner] = useState<React.ReactNode>();

  const [count, setCount] = useState<number>(0);
  const [refetchCount, setRefetchCount] = useState(false);

  const [countBookmark, setBookmarkCount] = useState<number>(0);
  const [refetchCountBookmark, setRefetchCountBookmark] = useState(false);

  const [orgID, setOrgID] = useState('');

  useEffect(() => {
    setsubscriptionPlan(props.subscriptionInfo);
  }, [props.subscriptionInfo]);

  useEffect(() => {
    if (isPIService && !refetchCount) {
      let subscription: Subscription;
      subscription = PIClient.getCountCart().subscribe(
        (res) => {
          setCount(res.data);
          setRefetchCount(false);
        },
        (err) => console.error(err),
      );

      return () => {
        subscription && subscription.unsubscribe();
        setRefetchCount(false);
      };
    }
  }, [isPIService, refetchCount]);

  const reFetch = useCallback(() => {
    setRefetchCount((curr) => !curr);
  }, []);

  useEffect(() => {
    if (isPIService && !refetchCountBookmark) {
      let subscription: Subscription;
      subscription = PIClient.getCountBookmark().subscribe(
        (res) => {
          setBookmarkCount(res.data);
          setRefetchCountBookmark(false);
        },
        (err) => console.error(err),
      );

      return () => {
        subscription && subscription.unsubscribe();
        setRefetchCountBookmark(false);
      };
    }
  }, [isPIService, refetchCountBookmark]);

  const reFetchCountBookmark = useCallback(() => {
    setRefetchCountBookmark((curr) => !curr);
  }, []);

  useEffect(() => {
    if (updateQuotationInfo?.erfxNo && updateQuotationInfo?.erfxType) {
      window.localStorage.setItem(
        LocalStorageName.UPDATE_QUOTATION_INFO,
        CommonHelper.base64Encoder(JSON.stringify(updateQuotationInfo) + ''),
      );
    }

    window.addEventListener('storage', boardcastUpdateQuotationInfoEventListener);

    return () => {
      window.removeEventListener('storage', boardcastUpdateQuotationInfoEventListener);
    };
  }, [updateQuotationInfo]);

  useEffect(() => {
    updateQuotationInfoSetup();

    // Mobile Size calculation
    setIsMobileSize(window.innerWidth <= MobileWidthSize);
    const handleMobileSize = (ev: UIEvent) => {
      const _isMobileSize = window.innerWidth <= MobileWidthSize;
      setIsMobileSize(_isMobileSize);
    };
    window.addEventListener('resize', handleMobileSize);

    setIsTabletSize(window.innerWidth <= TabletWidthSize);
    const handleTabletSize = (ev: UIEvent) => {
      const _isTabletSize = window.innerWidth <= TabletWidthSize;
      setIsTabletSize(_isTabletSize);
    };
    window.addEventListener('resize', handleTabletSize);

    return () => {
      window.removeEventListener('resize', handleMobileSize);
      window.removeEventListener('resize', handleTabletSize);
    };
  }, []);

  useEffect(() => {
    currencyCodeOptions.length > 0 && setCurrencyCodeSelected(currencyCodeSelected);
  }, [currencyCodeOptions]);

  const updateQuotationInfoSetup = () => {
    if (window.localStorage.getItem(LocalStorageName.UPDATE_QUOTATION_INFO)) {
      const updateQuotation: UpdateQuotationInf = JSON.parse(
        CommonHelper.base64Decoder(
          window.localStorage.getItem(LocalStorageName.UPDATE_QUOTATION_INFO) + '',
        ),
      );
      setUpdateQuotationInfo(updateQuotation);
    } else {
      setUpdateQuotationInfo({});
    }
  };

  const boardcastUpdateQuotationInfoEventListener = () => {
    updateQuotationInfoSetup();
  };

  const resetUpdateQuotationInfoOnAlertBar = () => {
    window.localStorage.removeItem(LocalStorageName.UPDATE_QUOTATION_INFO);

    window.addEventListener('storage', boardcastUpdateQuotationInfoEventListener);

    return () => {
      window.removeEventListener('storage', boardcastUpdateQuotationInfoEventListener);
    };
  };

  const setCurrencyCodeSelected = (val: CurrencyCodeOption | undefined) => {
    const valueSubmitted = val && currencyCodeOptions.includes(val) ? val : currencyCodeOptions[0];
    _setCurrencyCodeSelected(valueSubmitted);
    if (valueSubmitted) {
      localStorage.setItem(CURRENCY_LOCAL_STORAGE_KEY, valueSubmitted);
    } else {
      localStorage.removeItem(CURRENCY_LOCAL_STORAGE_KEY);
    }
  };

  return (
    <EMarketplaceLayoutContext.Provider
      value={{
        title,
        setTitle,
        navbar,
        setNavbar,
        breadcrumb,
        setBreadcrumb,
        isMobileSize,
        isTabletSize,
        navMenu,
        setNavMenu,
        role,
        setRole,
        updateQuotationInfo,
        setUpdateQuotationInfo,
        resetUpdateQuotationInfoOnAlertBar,
        currencyCodeSelected: currencyCodeOptions.length > 0 ? currencyCodeSelected : undefined,
        setCurrencyCodeSelected,
        currencyCodeOptions,
        setCurrencyCodeOptions,
        cartEventLastUpdate,
        setCartEventLastUpdate,
        activeMenuModule,
        setActiveMenuModule,
        banner,
        setBanner,
        count,
        reFetch,
        countBookmark,
        reFetchCountBookmark,
        orgID,
        setOrgID,
        subscriptionPlan,
      }}
    >
      {props.children}
    </EMarketplaceLayoutContext.Provider>
  );
};

export const useEMarketplaceLayoutContext = () => {
  return useContext(EMarketplaceLayoutContext) as EMarketplaceLayoutContextType;
};
